<template>
	<div>
		<div v-if="data.length">
			<ja-card
				class="card--selectable border-radius--0 mb--0"
				:selected="isAllItemsSelected"
				@click.native="onSelectAllItems(data)"
			>
				<ja-multiselect-circle name="select-item" :selected="true" />
				<div class="info">
					<div class="info-title">{{ $t('selectAll') }}</div>
				</div>
			</ja-card>
			<ja-card
				v-for="(item, index) in data"
				:key="index"
				class="card--selectable border-radius--0 mb--0"
				:selected="isItemSelected(item)"
				@click.native="onItemClick(item)"
			>
				<ja-multiselect-circle
					:selected="isItemSelected(item)"
					name="select-item"
				/>
				<div class="info">
					<div class="info-title">{{ item.title }}</div>
					<div class="info-subtitle">{{ item.subtitle }}</div>
				</div>
			</ja-card>
		</div>
		<div v-else class="text--center">
			<h4 class="heading-4">{{ $t('noResults') }}</h4>
		</div>
	</div>
</template>

<script>
import MultiselectCircle from './MultiselectCircle';
import { Card } from '@ui';
import isEqual from 'lodash/isEqual';

export default {
	components: {
		jaCard: Card,
		jaMultiselectCircle: MultiselectCircle
	},
	props: {
		items: {
			type: Array,
			default: () => []
		},
		preselectedItems: {
			type: Array,
			default: () => []
		},
		itemOptions: {
			type: Object,
			default: () => {}
		},
		page: {
			type: Number,
			default: 1
		},
		clearOnUpdate: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			data: [],
			selectedData: [],
			mappedItemProperties: {
				title: '',
				subtitle: ''
			},
			options: {
				selectionId: 'id',
				titleProperties: [],
				subtitleProperties: []
			},
			isAllItemsSelected: false,
			currentPage: 1
		};
	},
	computed: {
		selectionId() {
			return this.options.selectionId;
		}
	},
	watch: {
		items() {
			this.dataAdapter();
			if (this.clearOnUpdate) {
				this.isAllItemsSelected = false;
				this.selectedData = [];
			}
		},
		page() {
			if (this.page !== this.currentPage && this.isAllItemsSelected) {
				this.isAllItemsSelected = false;
			}
		}
	},
	mounted() {
		if (this.items) {
			this.dataAdapter();
		}
	},
	methods: {
		dataAdapter() {
			// Make sure options are up-to-date before map
			this.updateOptions();

			this.updateSelectedItems();

			this.data = this.items.map(item => {
				return {
					...item,
					title: this.setItemProperties(item, 'title'),
					subtitle: this.setItemProperties(item, 'subtitle')
				};
			});

			this.currentPage = this.page;
		},
		setItemProperties(item, itemProperty) {
			const propertyValues = this.options[`${itemProperty}Properties`];
			const itemValues = [];

			if (!propertyValues) {
				return item[itemProperty];
			}

			propertyValues.forEach(property => itemValues.push(item[property]));

			return itemValues.join(' ');
		},
		isItemSelected(item) {
			return (
				this.selectedData.findIndex(
					selectedItem =>
						selectedItem[this.selectionId] === item[this.selectionId]
				) !== -1 || item.isSelected
			);
		},
		getSelectedItemIndex(item) {
			return this.selectedData.findIndex(selectedItem => {
				return selectedItem[this.selectionId] === item[this.selectionId];
			});
		},
		onSelectAllItems(data) {
			this.isAllItemsSelected = !this.isAllItemsSelected;

			const isSelectedItemsEqual = isEqual(this.selectedData, data);

			if (!this.isAllItemsSelected || isSelectedItemsEqual) {
				this.selectedData = [];
			} else {
				this.selectedData = [...this.selectedData, ...data];
			}

			this.$emit('selectedItems', this.selectedData);
		},
		onItemClick(item) {
			if (this.isAllItemsSelected) {
				this.isAllItemsSelected = !this.isAllItemsSelected;
			}

			const selectedItemIndex = this.getSelectedItemIndex(item);

			if (selectedItemIndex !== -1) {
				this.selectedData.splice(selectedItemIndex, 1);
			} else {
				this.selectedData.push(item);
			}

			this.$emit('selectedItems', this.selectedData);
		},
		updateOptions() {
			this.options = { ...this.options, ...this.itemOptions };
		},
		updateSelectedItems() {
			this.selectedData = this.preselectedItems;
		}
	}
};
</script>

<style lang="scss" scoped>
.card--selectable {
	padding: 16px;
	display: flex;
	min-height: 64px;
	border: 0;
	border-bottom: 1px solid $table-border;
	align-items: center;

	&:last-of-type {
		border-bottom: 0;
	}

	&:hover {
		cursor: pointer;
	}

	&.selected {
		background-color: $hover-option-bgcolor;
	}
	.info {
		display: block;
		color: $font-header;
		letter-spacing: 0.5px;
		&-title {
			font-family: RobotoMedium;
			font-size: 16px;
			font-weight: 500;
			line-height: 20px;

			@media (min-width: $breakpoint-mid) {
				font-size: 18px;
			}
		}

		&-subtitle {
			font-family: RobotoRegular;
			font-size: 12px;
			line-height: 24px;
			@media (min-width: $breakpoint-mid) {
				font-size: 16px;
			}
		}
	}
}
</style>
